// =============================================================================
// PURPOSE / IKIGAI TYPES
// =============================================================================

export interface IkigaiAnswers {
  // Cirkel 1: Waar hou je van?
  love_timeflies: string;      // Waar verlies je het tijdsbesef in?
  love_energy: string;         // Welke activiteiten geven jou energie?
  love_unpaid: string;         // Wat zou je doen ook als je er niet voor betaald werd?

  // Cirkel 2: Waar ben je goed in?
  goodat_help: string;         // Waar vragen mensen jou steeds om hulp bij?
  goodat_others: string;       // Welke vaardigheden vinden anderen moeilijk maar jij niet?
  goodat_learn: string;        // Wat leer jij sneller dan de meeste mensen?

  // Cirkel 3: Wat heeft de wereld nodig?
  world_problem: string;       // Welk probleem zie jij dat jou raakt?
  world_change: string;        // Welke verandering wil jij zien in je vakgebied?
  world_for: string;           // Voor wie wil jij er zijn?

  // Cirkel 4: Waarvoor kun je betaald worden?
  paid_earned: string;         // Waar heb jij al eens geld mee verdiend of kunnen verdienen?
  paid_value: string;          // Welke dienst of product zou mensen genoeg waard zijn?
  paid_market: string;         // Wat ziet de markt in jou dat jij soms zelf over het hoofd ziet?
}

export interface IkigaiData {
  id?: string;
  userId: string;
  answers: IkigaiAnswers;
  purposeSummary?: string;     // Gegenereerde samenvatting (optioneel)
  createdAt: string;           // ISO-8601
  updatedAt: string;           // ISO-8601
}

export type IkigaiFormStep =
  | 'intro'
  | 'love'
  | 'goodat'
  | 'world'
  | 'paid'
  | 'result';

export interface IkigaiStepConfig {
  step: IkigaiFormStep;
  titleNL: string;
  subtitleNL: string;
  color: string;              // tailwind bg color class
  borderColor: string;        // tailwind border color class
  textColor: string;          // tailwind text color class
  questions: {
    field: keyof IkigaiAnswers;
    labelNL: string;
    placeholderNL: string;
  }[];
}

export const IKIGAI_STEPS: IkigaiStepConfig[] = [
  {
    step: 'love',
    titleNL: 'Waar hou je van?',
    subtitleNL: 'Activiteiten en onderwerpen die jou energie geven en je raken',
    color: 'bg-rose-50',
    borderColor: 'border-rose-200',
    textColor: 'text-rose-700',
    questions: [
      {
        field: 'love_timeflies',
        labelNL: 'Waar verlies je het tijdsbesef in?',
        placeholderNL: 'Beschrijf activiteiten waarbij uren als minuten voelen...',
      },
      {
        field: 'love_energy',
        labelNL: 'Welke activiteiten geven jou energie in plaats van dat ze kosten?',
        placeholderNL: 'Denk aan werk of hobby\'s waarbij je daarna opgeladen voelt...',
      },
      {
        field: 'love_unpaid',
        labelNL: 'Wat zou je doen ook als je er niet voor betaald werd?',
        placeholderNL: 'Als geld geen rol speelde, waar zou je dan je tijd in steken?',
      },
    ],
  },
  {
    step: 'goodat',
    titleNL: 'Waar ben je goed in?',
    subtitleNL: 'Vaardigheden en talenten die jou onderscheiden',
    color: 'bg-amber-50',
    borderColor: 'border-amber-200',
    textColor: 'text-amber-700',
    questions: [
      {
        field: 'goodat_help',
        labelNL: 'Waar vragen mensen jou steeds om hulp bij?',
        placeholderNL: 'Welke vragen of problemen leggen mensen bij jou neer?',
      },
      {
        field: 'goodat_others',
        labelNL: 'Welke vaardigheden vinden anderen moeilijk, maar jij niet?',
        placeholderNL: 'Dingen die voor jou vanzelfsprekend zijn maar anderen moeite kosten...',
      },
      {
        field: 'goodat_learn',
        labelNL: 'Wat leer jij sneller dan de meeste mensen?',
        placeholderNL: 'Gebieden waar je snel nieuwe dingen oppikt of patronen herkent...',
      },
    ],
  },
  {
    step: 'world',
    titleNL: 'Wat heeft de wereld nodig?',
    subtitleNL: 'De bijdrage die jij wil leveren aan anderen',
    color: 'bg-teal-50',
    borderColor: 'border-teal-200',
    textColor: 'text-teal-700',
    questions: [
      {
        field: 'world_problem',
        labelNL: 'Welk probleem zie jij om je heen dat jou raakt?',
        placeholderNL: 'Wat zie jij in de wereld of jouw vakgebied dat anders zou moeten?',
      },
      {
        field: 'world_change',
        labelNL: 'Welke verandering wil jij zien in jouw vakgebied of community?',
        placeholderNL: 'Als jij de wereld of een sector kon verbeteren, hoe zou dat eruitzien?',
      },
      {
        field: 'world_for',
        labelNL: 'Voor wie wil jij er zijn?',
        placeholderNL: 'Beschrijf de mensen of groep die jij wil helpen of ondersteunen...',
      },
    ],
  },
  {
    step: 'paid',
    titleNL: 'Waarvoor kun je betaald worden?',
    subtitleNL: 'De economische waarde die jij kunt bieden',
    color: 'bg-violet-50',
    borderColor: 'border-violet-200',
    textColor: 'text-violet-700',
    questions: [
      {
        field: 'paid_earned',
        labelNL: 'Waar heb jij al eens geld mee verdiend of kunnen verdienen?',
        placeholderNL: 'Bestaande inkomstenbronnen of diensten waarvoor mensen hebben betaald...',
      },
      {
        field: 'paid_value',
        labelNL: 'Welke dienst of product zou mensen genoeg waard zijn om voor te betalen?',
        placeholderNL: 'Wat zou je kunnen aanbieden dat mensen graag willen kopen?',
      },
      {
        field: 'paid_market',
        labelNL: 'Wat ziet de markt in jou dat jij soms zelf over het hoofd ziet?',
        placeholderNL: 'Complimenten die je regelmatig krijgt over je werk of aanpak...',
      },
    ],
  },
];

export const STEP_ORDER: IkigaiFormStep[] = ['intro', 'love', 'goodat', 'world', 'paid', 'result'];

export const INTERSECTION_LABELS = {
  love_goodat: { nl: 'Passie', description: 'Wat je liefhebt en goed in bent' },
  goodat_paid: { nl: 'Professie', description: 'Waar je goed in bent en voor betaald wordt' },
  paid_world: { nl: 'Roeping', description: 'Waarvoor betaald en wat de wereld nodig heeft' },
  world_love: { nl: 'Missie', description: 'Wat de wereld nodig heeft en jij liefhebt' },
  center: { nl: 'Purpose', description: 'Het kruispunt van alle vier — jouw reden van bestaan' },
};
